const FALLBACK_IMAGE = 'https://eventsbilletterie.com/assets/images/facadeEvents.png';
const SAFE_DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=\s]+$/i;
const SAFE_RAW_BASE64 = /^[A-Za-z0-9+/=\s]{200,}$/;

function isTrustedEventsHost(hostname) {
    const host = String(hostname || '').toLowerCase().replace(/\.$/, '');
    if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return true;
    return host === 'eventsbilletterie.com' || host.endsWith('.eventsbilletterie.com');
}

function trustedHttpsUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
        const parsed = new URL(value.trim());
        if (parsed.username || parsed.password) return null;
        const loopback = parsed.hostname === 'localhost'
            || parsed.hostname === '127.0.0.1'
            || parsed.hostname === '[::1]';
        if (parsed.protocol === 'http:' && !loopback) return null;
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
        if (!isTrustedEventsHost(parsed.hostname)) return null;
        return parsed.toString();
    } catch {
        return null;
    }
}

function httpsImage(value) {
    return trustedHttpsUrl(value) || FALLBACK_IMAGE;
}

/** Sérialise le JSON-LD sans permettre de casser </script> */
export function serializeJsonLd(payload) {
    return JSON.stringify(payload)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
}

export function eventBannerSrc(event) {
    const banner = event?.banner;
    if (!banner || typeof banner !== 'string') return FALLBACK_IMAGE;
    const trusted = trustedHttpsUrl(banner);
    if (trusted) return trusted;
    if (SAFE_DATA_IMAGE.test(banner)) return banner;
    if (!banner.includes('<') && SAFE_RAW_BASE64.test(banner)) {
        return `data:image/jpeg;base64,${banner.replace(/\s/g, '')}`;
    }
    return FALLBACK_IMAGE;
}

export function toIsoDateTime(date, hour) {
    if (!date) return null;
    const raw = String(date).trim();
    const time = hour ? String(hour).trim() : '';
    let isoCandidate = raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw) && time) {
        const hm = time.length === 5 ? `${time}:00` : time;
        isoCandidate = `${raw}T${hm}`;
    }
    const parsed = new Date(isoCandidate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString();
}

export function buildPublicEventsItemListJsonLd(events, listUrl) {
    const listPageUrl = trustedHttpsUrl(listUrl) || 'https://eventsbilletterie.com/events';
    const items = (events || []).slice(0, 10).map((event, index) => {
        const start = event.start_at || toIsoDateTime(event.date_range || event.date, event.hour);
        const end = event.end_at || start;
        const url = trustedHttpsUrl(event.url || event.lien_achat) || undefined;
        const prices = (event.pricing || [])
            .map((p) => Number(p.price))
            .filter((n) => Number.isFinite(n) && n >= 0);
        const low = prices.length ? Math.min(...prices) : 0;
        const high = prices.length ? Math.max(...prices) : low;
        const inStock = Number(event.tickets?.remaining) > 0;
        const performerName = event.artist || event.organizer || 'Events Billetterie';
        const place = event.place || 'Côte d’Ivoire';

        const item = {
            '@type': 'Event',
            name: event.title,
            description: event.description || `Événement ${event.title} à ${place}`,
            image: httpsImage(event.banner),
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            eventStatus: 'https://schema.org/EventScheduled',
            location: {
                '@type': 'Place',
                name: place,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: place,
                    addressLocality: /abidjan/i.test(place) ? 'Abidjan' : place,
                    addressCountry: 'CI',
                },
            },
            organizer: {
                '@type': 'Organization',
                name: event.organizer || 'Events Billetterie',
            },
            performer: {
                '@type': 'PerformingGroup',
                name: performerName,
            },
            offers: {
                '@type': 'AggregateOffer',
                priceCurrency: 'XOF',
                lowPrice: low,
                highPrice: high,
                availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            },
        };

        if (url) {
            item.url = url;
            item.offers.url = url;
        }

        if (start) {
            item.startDate = start;
            item.endDate = end || start;
        }

        return {
            '@type': 'ListItem',
            position: index + 1,
            item,
        };
    });

    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Événements en Côte d’Ivoire',
        description: 'Liste des événements disponibles en Côte d’Ivoire',
        url: listPageUrl,
        numberOfItems: items.length,
        itemListElement: items,
    };
}
